import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { formatUserRole } from '@/lib/utils';
import type { Invitation } from '@/services/types';

interface InvitationTableColumnsProps {
  onDeleteInvitation: (invitation: Invitation & { id?: string }) => void;
  isAdmin: boolean;
}

export const createInvitationColumns = ({ onDeleteInvitation, isAdmin }: InvitationTableColumnsProps): ColumnDef<Invitation & { id?: string }>[] => [
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.getValue<string>('email');
      return (
        <div className='flex items-center space-x-3'>
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-muted'>
            <span className='text-sm font-medium'>
              {email?.charAt(0)?.toUpperCase() || 'I'}
            </span>
          </div>
          <div className='font-medium'>{email}</div>
        </div>
      );
    }
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => {
      const role = row.getValue<string>('role');
      
      return (
        <Badge variant={'secondary'}>
          {formatUserRole(role)}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue<string>('status');
      return (
        <Badge variant='outline'>
          {status === 'pending' ? 'Pending' : 'Accepted'}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Invited',
    cell: ({ row }) => {
      const date = row.getValue<Date>('createdAt');
      return (
        <div className='text-sm'>
          {date ? new Date(date).toLocaleDateString() : 'N/A'}
        </div>
      );
    }
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const invitation = row.original;
      
      if (!isAdmin) {
        return null;
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem 
              onClick={() => onDeleteInvitation(invitation)}
              className='text-red-600'
            >
              <Trash2 className='mr-2 h-4 w-4' />
              Delete Invitation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
  }
];